import {
    Title,
    Subtitle,
    HelpDescription,
    checkboxStyle,
    MainForm
} from './styles'
import React, { useEffect, useMemo, useState } from 'react'
import { Dispatch } from 'redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { ROUTES_NAMES } from 'constants/name.routes'
import ActionStatus from 'components/ActionStatus'
import {
    NotificationAction,
    NotificationReducer,
    NotificationType
} from 'types/notification'
import NotificationActions from 'actions/Notification'
import NotificationService from 'services/Notification'
import { UserDataType } from 'types/autentication'
import { RootReducerState } from 'types/global'
import { EditButton } from '../IshikawaDiagram/styles'
import { MdOutlineModeEditOutline } from 'react-icons/md'
import PreviewResult from './previewResult'
import { canEdit } from './helper'
import { hasAdverseEventsOrImageRisk } from 'utils/notifyEvents'
import { damage_level, isNoDamageOrNearMissOrNotifiableDamage } from 'utils/damage'
interface AccountabilityMatrixProps {
    tooltipAlert: string
    notification: NotificationType
    statusIsArchivedOrFinished: boolean
    currentUser: UserDataType
    notificationStatusState: string
}

const AccountabilityMatrix = ({
    notificationStatusState,
    tooltipAlert,
    notification,
    statusIsArchivedOrFinished,
    currentUser
}: AccountabilityMatrixProps) => {
    const [notAllowed, setNotAllowed] = useState<boolean>(false)
    const [unrealizedOptional, setUnrealizedOptional] = useState<boolean>(false)

    const { id } = useParams()
    const navigate = useNavigate()

    const notificationSelector: NotificationReducer = useSelector(
        (state: RootReducerState) => state.notificationsReducer
    )
    const notificationDispatch: Dispatch<NotificationAction> =useDispatch()

    const accountabilityMatrixMemo = useMemo(() => {
        if (notificationSelector?.accountabilityMatrix) {
            return notificationSelector?.accountabilityMatrix[0]
        }
    }, [notificationSelector?.accountabilityMatrix])

    useEffect(() => {
        console.log("Loading Accountability Matrix...")
        NotificationActions.getAccountabilityMatrixByNotificationId(notificationDispatch, id)
    }, [currentUser])

    useEffect(() => {
        if (!notAllowed) {
            setUnrealizedOptional(accountabilityMatrixMemo?.u_do_not_perform_optional == 'true')
        }
    }, [notAllowed, accountabilityMatrixMemo])


    const isSameLocation = useMemo(() => {
        return (
            currentUser?.locations &&
            currentUser?.locations?.filter(
                (i) => i.sys_id === notification?.location_incident?.value
            ).length > 0
        )
    }, [currentUser, notification])

    const isSameSector = useMemo(() => {
        return (
            currentUser?.locations &&
            currentUser?.locations
                ?.filter(
                    (i) => i.sys_id === notification?.location_incident?.value
                )[0]
                ?.sectors.filter(
                    (i) => i.sys_id === notification?.incident_sector?.value
                ).length > 0
        )
    }, [currentUser, notification])

    const loggedInUserIsResponsible = useMemo(() => {
        return (
            currentUser?.sys_id &&
            currentUser?.sys_id === notification?.u_assigned_to?.value
        )
    }, [currentUser, notification])

    useEffect(() => {
        const canEditForm = canEdit({
            loggedInUserIsResponsible,
            isSameLocation,
            isSameSector,
            notificationStatusState
        })
        const canYouEdit = canEditForm.includes(currentUser?.profile)
        if (canYouEdit && !statusIsArchivedOrFinished) {
            setNotAllowed(false)
        } else {
            setNotAllowed(true)
        }
    }, [
        loggedInUserIsResponsible,
        isSameLocation,
        isSameSector,
        notificationStatusState,
        statusIsArchivedOrFinished
    ])

    const hideOrShowCheckUnrealizedOptional = useMemo(() => {
        let imageRiskValue = notificationSelector.notificationById?.u_image_risk?.value
        let damage = notificationSelector.notificationById?.damage_level?.value
        const isImageRisk = imageRiskValue === 'true' || imageRiskValue === 'u_image_risk_yes'
        const hasNeverEvents = notificationSelector?.notificationById?.u_never_events_or_sentinel_event?.value == 'yes'

        const isOlder = notificationSelector?.notificationById?.u_probability_occurence?.value === ''
        if (isOlder) {
            const checkboxRequired = !isImageRisk && isNoDamageOrNearMissOrNotifiableDamage(damage)
            return checkboxRequired
        }

        const stateAccountMatrix = notificationSelector?.notificationProcessRequiredStepsRules?.accountabilityMatrixMandatory
        return !hasNeverEvents && ((!hasAdverseEventsOrImageRisk({ damageLevelValue: damage, imageRiskValue: imageRiskValue })
            || stateAccountMatrix === "Optional"))

    }, [currentUser])

    const onChangeCheckedNotPerform = (checkedValue: boolean) => {
        setUnrealizedOptional(checkedValue)

        let AccountabilityMatrixSN: any = {
            ...(accountabilityMatrixMemo?.sys_id
                ? { sys_id: accountabilityMatrixMemo?.sys_id }
                : {}),
            u_hospital_incident: id,
            u_do_not_perform_optional: checkedValue ? 'true' : 'false',
            u_question_01: '',
            u_question_02: '',
            u_question_03: '',
            u_question_04: '',
            u_question_05: '',
            u_question_06: '',
            u_question_07: '',
            u_question_08: '',
            u_question_09: '',
            u_question_10: '',
            u_matrix_result: ''
        }

        NotificationService.saveAccountabilityMatrix(AccountabilityMatrixSN)
            .then(() => {
                NotificationActions.getAccountabilityMatrixByNotificationId(
                    notificationDispatch,
                    id
                )
            })
            .catch((error) => {
                console.log('saveAccountabilityMatrix error', error)
            })
    }

    const navigateRoute = () => {
        navigate(`${ROUTES_NAMES.notifications}/accountability_matrix/${id}`)
    }
    const hideOrShowPreview = () => {
        if (accountabilityMatrixMemo) {
            return accountabilityMatrixMemo?.u_matrix_result !== ''
        }
    }

    return (
        <>
            <MainForm data-testid="test-accountabilitmatrix">
                <div className="px-8 flex flex-row gap-12 mt-1 min-h-[500px]">
                    <fieldset disabled={notAllowed}>
                        <div className="flex flex-col">
                            {tooltipAlert && (
                                <div
                                    className="px-6"
                                    data-testid="test-tooltip-alert-3"
                                >
                                    <ActionStatus
                                        text={tooltipAlert}
                                        status={1}
                                    />
                                </div>
                            )}
                            <div className="flex flex-row gap-10 mt-1 mb-10">
                                <div className="flex justify-center items-center gap-3">
                                    <Title>Matriz de responsabilização</Title>
                                </div>
                            </div>

                            <Subtitle>
                                A Matriz de Responsabilização será utilizada na análise de incidentes quando existir necessidade de definir e formalizar responsabilidades entre 
                                profissionais, equipes, setores ou áreas de apoio, especialmente em eventos que envolvam falhas de processo, comunicação ou interfaces assistenciais.
                            </Subtitle>
                            
                            <HelpDescription>
                                <p className="mb-2">Conforme a POLDOR 007, sua aplicação é indicada: </p>
                                <div className="w-full">
                                    <ul className="list-disc list-outside pl-5 marker:font-bold marker:text-lg space-y-2 leading-relaxed">
                                    <li>
                                        Em notificações classificadas como circunstância notificável, near miss, incidente sem dano, com dano leve ou incidente com dano moderado, quando a Matriz de Risco apontar maior complexidade organizacional (pontuação maior que 5)
                                    </li>
                                    <li>
                                        Em eventos com dano grave, óbito, evento sentinela/never event ou risco de imagem é obrigatório.
                                    </li>
                                    </ul>
                                </div>
                            </HelpDescription>

                            <div className="flex flex-row gap-12 mt-6 mb-10">
                                <EditButton
                                    type="button"
                                    className={`${unrealizedOptional || notAllowed
                                        ? 'bg-disabled text-textSelect font-normal'
                                        : ''
                                        }`}
                                    disabled={unrealizedOptional || notAllowed}
                                    onClick={navigateRoute}
                                >
                                    <div className="w-[202px] flex justify-center items-center gap-3">
                                        <MdOutlineModeEditOutline
                                            size={18}
                                            color={`${!unrealizedOptional ||
                                                notAllowed
                                                ? 'white'
                                                : '#4F4F4F'
                                                }`}
                                        />
                                        <span className="inline-block align-middle">
                                            {!accountabilityMatrixMemo?.sys_id
                                                ? 'Preencher matriz'
                                                : 'Editar matriz'}
                                        </span>
                                    </div>
                                </EditButton>

                                {hideOrShowCheckUnrealizedOptional &&
                                    <div className="mt-2 block min-h-[1.5rem] pl-[1.5rem]">
                                        <input
                                            onChange={(e) =>
                                                onChangeCheckedNotPerform(
                                                    e.target.checked
                                                )
                                            }
                                            checked={unrealizedOptional}
                                            name="u_do_not_perform_optional"
                                            className={`${checkboxStyle}  ${notAllowed
                                                ? 'opacity-40'
                                                : 'hover:cursor-pointer'
                                                }`}
                                            type="checkbox"
                                            id="checkUnrealizedOptional"
                                        />
                                        <label
                                            className={`inline-block pl-[0.15rem]  ${notAllowed
                                                ? 'opacity-40'
                                                : 'hover:cursor-pointer'
                                                }`}
                                            htmlFor="checkUnrealizedOptional">
                                            Não realizar o preenchimento
                                        </label>
                                    </div>}
                            </div>

                            {hideOrShowPreview() ? (
                                <PreviewResult
                                    accountabilityMatrix={
                                        accountabilityMatrixMemo
                                    }
                                ></PreviewResult>
                            ) : (
                                <></>
                            )}
                        </div>
                    </fieldset>
                </div>
            </MainForm>
        </>
    )
}

export { AccountabilityMatrix }
